/**
 * k-schema-search.js - multi-schema part search (issue #700)
 *
 * Reads theme/assets/finder-data.json (Codex-owned source of truth, already
 * synced to data/identification/finder-data.v1.json - no second index).
 * Drives the exact same selection state as a manual hotspot/row click by
 * dispatching 'krepite:activate-pos' on the schema root, handled in
 * k-hotspots.js. Never navigates outside the current model's schemas.
 *
 * K.init(searchRoot, schemaRoot) wires the input/results UI.
 * K.applyUrlSelection(schemaRoot) resolves ?ref=&repere= on page load.
 */

(function (global) {
  'use strict';

  var K = global.KSchemaSearch = global.KSchemaSearch || {};

  function normalize(s) {
    return (s || '').toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeOem(s) {
    if (global.KrepiteSearch && global.KrepiteSearch.normalizeOem) {
      return global.KrepiteSearch.normalizeOem(s);
    }
    return (s || '').toString()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '');
  }

  /* ── Ranking (spec §5) ────────────────────────────────────────────────
   * 1. OEM exact normalized
   * 2. OEM starting with the query
   * 3. every word of the query present in the name
   * 4. partial name match
   * 5. repère, only when the query is explicit ("repère 3")
   * A bare short number is never silently read as a repère. ─────────── */
  function rankResults(query, parts) {
    var qRaw = (query || '').trim();
    if (!qRaw) return [];
    var qDimension = global.KrepiteSearch && global.KrepiteSearch.parseDimensionQuery
      ? global.KrepiteSearch.parseDimensionQuery(qRaw)
      : null;
    var qOem = qDimension ? '' : normalizeOem(qRaw);
    var qName = normalize(qRaw);
    var qWords = qName.split(' ').filter(Boolean);
    var repereMatch = /^rep[eè]re\s+(\S+)$/i.exec(qRaw);
    var ranked = [];
    parts.forEach(function (p) {
      var oem = normalizeOem(p.reference_oem);
      var name = normalize(p.title);
      var rank = null;
      if (qDimension && global.KrepiteSearch && global.KrepiteSearch.partMatchesDimension
          && global.KrepiteSearch.partMatchesDimension(p, qDimension)) rank = 1;
      else if (qOem && oem && oem === qOem) rank = 1;
      else if (qOem && oem && oem.indexOf(qOem) === 0) rank = 2;
      else if (qWords.length && qWords.every(function (w) { return name.indexOf(w) !== -1; })) rank = 3;
      else if (qName && name.indexOf(qName) !== -1) rank = 4;
      // 31/10 : meme tolerance aux fautes que le moteur commun (« vermiculitte »).
      else if (qWords.length && global.KrepiteSearch && global.KrepiteSearch.fuzzyTokenMatch
          && qWords.every(function (w) { return global.KrepiteSearch.fuzzyTokenMatch(w, p.title || ''); })) rank = 4;
      else if (repereMatch && String(p.repere) === repereMatch[1]) rank = 5;
      if (rank !== null) ranked.push({ part: p, rank: rank });
    });
    ranked.sort(function (a, b) { return a.rank - b.rank; });
    return ranked.map(function (r) { return r.part; });
  }

  function withPreviewParam(url) {
    var previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (!previewThemeId) return url;
    var u = new URL(url, window.location.origin);
    u.searchParams.set('preview_theme_id', previewThemeId);
    return u.pathname + u.search;
  }

  /* Matches a #k-hs-data entry of the CURRENT schema against a normalized
   * OEM + position pair. Repère alone is never sufficient (spec §6). */
  function findHsEntry(schemaRoot, ref, repere) {
    var scriptEl = schemaRoot.querySelector('#k-hs-data');
    if (!scriptEl) return null;
    var data;
    try { data = JSON.parse(scriptEl.textContent); } catch (e) { return null; }
    if (!Array.isArray(data)) return null;
    var normRef = normalizeOem(ref);
    var found = null;
    data.forEach(function (hs) {
      if (found) return;
      var hsSku = normalizeOem(hs.sku || hs.part_number || '');
      var hsPos = String(hs.position != null ? hs.position : '');
      if (hsSku && normRef && hsSku === normRef && hsPos === String(repere)) found = hs;
    });
    return found;
  }

  /* Resolve ?ref=&repere= against the current schema's own hotspot data and
   * activate the exact match. Safe no-op if the params are absent/unproven. */
  K.applyUrlSelection = function (schemaRoot) {
    if (!schemaRoot) return;
    var params = new URLSearchParams(window.location.search);
    var ref = params.get('ref');
    var repere = params.get('repere');
    if (!ref || !repere) return;
    var entry = findHsEntry(schemaRoot, ref, repere);
    if (!entry) return;
    var pos = entry.position != null ? entry.position : repere;
    schemaRoot.dispatchEvent(new CustomEvent('krepite:activate-pos', { detail: { pos: pos } }));
  };

  /* 31/10 : le champ est le champ commun (snippets/k-champ-recherche.liquid, monte par
   * k-live-search.js, cadre sur le modele). Ce module n'a plus de champ a lui : il ajoute en
   * tete des suggestions le groupe « Sur les schémas de ce modèle » (clic = repere active,
   * ou schema voisin), et Entree affiche la liste complete sous le champ (une seule
   * correspondance : selection directe ; aucune : le champ commun envoie au Finder). */
  K.init = function (searchRoot, schemaRoot) {
    if (!searchRoot || !schemaRoot) return;
    var modelHandle   = searchRoot.getAttribute('data-model-handle') || '';
    var schemaHandle  = searchRoot.getAttribute('data-schema-handle') || '';
    var dataUrl       = searchRoot.getAttribute('data-finder-data-url');
    var verifyUrl     = searchRoot.getAttribute('data-verify-url') || '/pages/trouver-ma-piece';
    var form          = searchRoot.querySelector('[data-k-live-search-inline]');
    var input         = searchRoot.querySelector('[data-k-live-search-input]');
    var resultsBox    = searchRoot.querySelector('.k-schema-search__results');
    if (!form || !input || !resultsBox || !dataUrl) return;

    var partsCache = null;
    var partsPromise = null;

    function chargeurPartage() {
      return new Promise(function (resolve, reject) {
        var essais = 0;
        (function attendre() {
          if (window.KrepiteSearch && window.KrepiteSearch.loadFinderData) {
            resolve(window.KrepiteSearch.loadFinderData());
          } else if (++essais > 60) {
            reject(new Error('KrepiteSearch indisponible'));
          } else {
            window.setTimeout(attendre, 50);
          }
        })();
      });
    }

    function loadParts() {
      if (partsCache) return Promise.resolve(partsCache);
      if (partsPromise) return partsPromise;
      // Chargeur PARTAGE (k-search-core) : un seul telechargement par page, et
      // la projection compacte y est rehydratee (P0 Finder 2026-10-13).
      partsPromise = chargeurPartage().then(function (json) {
        var model = json && json.models && json.models[modelHandle];
        partsCache = (model && Array.isArray(model.candidate_parts)) ? model.candidate_parts : [];
        return partsCache;
      }).catch(function () {
        partsCache = [];
        return partsCache;
      });
      return partsPromise;
    }

    function fermerPanneau() {
      var panel = form.querySelector('[data-k-live-search-panel]');
      if (panel) panel.hidden = true;
    }

    function clearResults() {
      resultsBox.innerHTML = '';
    }

    function urlSchema(part) {
      return withPreviewParam('/collections/' + part.schema_handle
        + '?ref=' + encodeURIComponent(part.reference_oem || '')
        + '&repere=' + encodeURIComponent(part.repere != null ? part.repere : ''));
    }

    function selectSameSchema(part) {
      schemaRoot.dispatchEvent(new CustomEvent('krepite:activate-pos', { detail: { pos: part.repere } }));
      clearResults();
      fermerPanneau();
    }

    function selectPart(part) {
      if (part.schema_handle === schemaHandle) selectSameSchema(part);
      else window.location.href = urlSchema(part);
    }

    function renderResultButton(part) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'k-schema-search__result';
      var name = document.createElement('span');
      name.className = 'k-schema-search__result-name';
      name.textContent = part.title || 'Pièce';
      var meta = document.createElement('span');
      meta.className = 'k-schema-search__result-meta';
      meta.textContent = 'Réf. ' + (part.reference_oem || '-')
        + ' · Schéma ' + part.schema_number + ' · Repère ' + part.repere;
      btn.appendChild(name);
      btn.appendChild(meta);
      btn.addEventListener('click', function () { selectPart(part); });
      li.appendChild(btn);
      return li;
    }

    function renderResults(parts) {
      clearResults();
      if (!parts.length) return;
      var ul = document.createElement('ul');
      ul.className = 'k-schema-search__result-list';
      parts.forEach(function (part) { ul.appendChild(renderResultButton(part)); });
      resultsBox.appendChild(ul);
      var aide = document.createElement('p');
      aide.className = 'k-schema-search__help';
      var a = document.createElement('a');
      a.href = verifyUrl;
      a.textContent = 'Demander une identification assistée';
      aide.appendChild(document.createTextNode('Pas la bonne pièce ? '));
      aide.appendChild(a);
      resultsBox.appendChild(aide);
    }

    // Filtre local : nombre de correspondances dans les schemas du modele, lu par le champ
    // commun (Entree reste sur la page tant qu'il y en a ; sinon envoi au Finder).
    input.addEventListener('focus', function () { loadParts(); });
    input.addEventListener('input', function () {
      var query = input.value;
      clearResults();
      loadParts().then(function (parts) {
        if (input.value !== query) return;
        form.setAttribute('data-k-filtre-resultats', String(rankResults(query, parts).length));
      });
    });

    form.addEventListener('k-live-search:rendu', function (event) {
      var q = event.detail.q;
      var results = event.detail.results;
      var ls = window.KrepiteLiveSearch;
      loadParts().then(function (parts) {
        if (!ls || !results || input.value.trim() !== q) return;
        var ranked = rankResults(q, parts).slice(0, 6);
        if (!ranked.length) return;
        var vide = results.querySelector('.k-live-search__empty');
        if (vide) vide.remove();
        // Une piece deja proposee par le groupe des schemas n'est pas repetee plus bas.
        var chemin = function (url) {
          try { return new URL(url, window.location.origin).pathname.replace(/\/$/, ''); } catch (e) { return ''; }
        };
        var dejaVues = {};
        ranked.forEach(function (part) { if (part.url) dejaVues[chemin(part.url)] = true; });
        Array.prototype.slice.call(results.querySelectorAll('[data-k-live-search-option]')).forEach(function (opt) {
          if (dejaVues[chemin(opt.getAttribute('href'))]) opt.remove();
        });
        Array.prototype.slice.call(results.querySelectorAll('.k-live-search__group')).forEach(function (g) {
          if (!g.querySelector('[data-k-live-search-option]')) g.remove();
        });
        var groupe = document.createElement('div');
        groupe.className = 'k-live-search__group';
        var titre = document.createElement('p');
        titre.className = 'k-live-search__group-title';
        titre.textContent = 'Sur les schémas de ce modèle';
        groupe.appendChild(titre);
        ranked.forEach(function (part) {
          var item = Object.assign({}, part, { kind: 'part', url: urlSchema(part), model_label: 'Schéma ' + part.schema_number });
          var option = ls.createResult(item, null);
          option.addEventListener('click', function (e) {
            if (part.schema_handle !== schemaHandle) return;
            e.preventDefault();
            selectSameSchema(part);
          });
          groupe.appendChild(option);
        });
        var portee = results.querySelector('.k-live-search__portee');
        results.insertBefore(groupe, portee ? portee.nextSibling : results.firstChild);
      });
    });

    form.addEventListener('k-champ:valider', function () {
      loadParts().then(function (parts) {
        var ranked = rankResults(input.value, parts);
        if (ranked.length === 1) {
          selectPart(ranked[0]);
        } else if (ranked.length > 1) {
          // Plusieurs resultats : jamais de navigation automatique, la liste complete
          // s'affiche sous le champ et le focus passe sur la premiere ligne.
          renderResults(ranked);
          var first = resultsBox.querySelector('.k-schema-search__result');
          if (first) first.focus();
        }
      });
    });
  };

}(window));
